const SEAFOOD_PLAN_KEY = "seafood-plan";

// Keep funnel telemetry separate from checkout behavior. The base PostHog
// snippet queues captures while its SDK loads, and analytics must never throw.
function captureSeafoodPlanProgress(step, outcome, properties) {
  try {
    if (typeof window === "undefined" || !window.posthog ||
        typeof window.posthog.capture !== "function") return;
    window.posthog.capture("seafood_plan_progress", {
      funnel: "seafood_plan",
      step: step,
      outcome: outcome,
      ...(properties || {}),
    }, {
      transport: "sendBeacon",
      send_instantly: outcome === "cart_ready",
    });
  } catch (_) { /* analytics must never interrupt a shopper */ }
}

// Frequency-default site test (test T4 in the data repo's
// marketing/email/specs/1x-to-second-order-ab-tests.md): does preselecting
// every 6 weeks instead of every month lift checkout starts? Inert until a
// PostHog flag with this key exists and rolls out: an unknown or off flag
// returns undefined, which keeps the 4-week default.
const FREQUENCY_DEFAULT_FLAG = "seafood-plan-frequency-default";
const FREQUENCY_DEFAULT_VARIANT_WEEKS = { control: "4", "six-weeks": "6" };
const FREQUENCY_DEFAULT_FLAG_TIMEOUT_MS = 1000;

function frequencyWeeksForVariant(variant) {
  return Object.prototype.hasOwnProperty.call(FREQUENCY_DEFAULT_VARIANT_WEEKS, variant)
    ? FREQUENCY_DEFAULT_VARIANT_WEEKS[variant]
    : null;
}

// Resolves the shopper's variant, or null when they are not enrolled. Only a
// value from this page load's flag request counts (`fresh`), never one cached
// from an earlier visit. A fresh read is undefined both before /flags responds
// and when the flag is inactive (omitted from the response), so undefined keeps
// waiting for a later callback or the timeout; only a fresh `false` settles
// early. The flag is read with an exposure ($feature_flag_called) only once a
// fresh variant exists and the shopper is still eligible, so nobody who already
// picked a frequency is exposed or switched, and a failed or paused refresh
// never enrolls anyone. Callers skip all of this unless the section marks the
// experiment live, so an inactive flag costs shoppers no wait.
function resolveFrequencyDefaultVariant(posthog, timeoutMs, stillEligible) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (variant) => {
      if (settled) return;
      settled = true;
      resolve(variant);
    };
    try {
      if (!posthog || typeof posthog.onFeatureFlags !== "function") return finish(null);
      setTimeout(() => finish(null), timeoutMs);
      posthog.onFeatureFlags((_flags, _variants, context) => {
        if (settled) return;
        if (context && context.errorsLoading) return finish(null);
        try {
          if (stillEligible && !stillEligible()) return finish(null);
          const fresh = posthog.getFeatureFlag(FREQUENCY_DEFAULT_FLAG, { fresh: true, send_event: false });
          // A 200 /flags response can still report errors_while_computing_flags,
          // and the SDK then keeps the failed key's cached value. The SDK records
          // that response's errors in $feature_flag_errors: any error, no enrollment.
          const flagErrors = typeof posthog.get_property === "function"
            ? posthog.get_property("$feature_flag_errors") : undefined;
          if (Array.isArray(flagErrors) && flagErrors.length) return finish(null);
          if (frequencyWeeksForVariant(fresh)) {
            posthog.getFeatureFlag(FREQUENCY_DEFAULT_FLAG, { fresh: true }); // logs the exposure
            return finish(fresh);
          }
          // A fresh value that isn't a variant (false: this person is outside
          // the rollout) is conclusive; undefined is not, so keep waiting.
          if (fresh !== undefined) finish(null);
        } catch (_) { finish(null); }
      });
    } catch (_) { finish(null); }
  });
}

// WELCOME20 is a flat $20-off DiscountCodeBasic in Admin, despite the name.
// Keep this in cents and in sync with box-size-logic.js.
const WELCOME20_FLAT_OFF_CENTS = 2000;

/**
 * Preview-only price math for acquisition codes, in cents.
 *
 * This MUST mirror what checkout actually applies. WELCOME20 was previously
 * previewed as `price * 0.8` (20% off), but the Admin discount is a flat $20
 * DiscountCodeBasic — so the funnel over-promised on every box above $100, and
 * with AOV around $214 it routinely showed roughly double the real discount.
 * Codes with no preview rule return the price untouched rather than guessing.
 *
 * (100OFF previously had a commented-out per-frequency ladder here; it never
 * ran, so it is intentionally not carried forward.)
 */
function applyPreviewDiscountCents(priceCents, code) {
  if (code === "WELCOME20") {
    return Math.max(0, priceCents - WELCOME20_FLAT_OFF_CENTS);
  }
  return priceCents;
}

const BOX_WEEKS_TO_PLAN_ID = new Map();
BOX_WEEKS_TO_PLAN_ID.set("4", "693379957010");
BOX_WEEKS_TO_PLAN_ID.set("6", "693380022546");
BOX_WEEKS_TO_PLAN_ID.set("8", "693380088082");

const SEAFOOD_TYPE_TO_PRODUCT_HANDLE = new Map();
SEAFOOD_TYPE_TO_PRODUCT_HANDLE.set("salmon", "wild-caught-alaskan-salmon-box");
SEAFOOD_TYPE_TO_PRODUCT_HANDLE.set(
  "whitefish",
  "wild-caught-white-fish-box",
);
SEAFOOD_TYPE_TO_PRODUCT_HANDLE.set(
  "crab",
  "alaskan-crab-variety-subscription-box",
);
SEAFOOD_TYPE_TO_PRODUCT_HANDLE.set(
  "fishvariety",
  "wild-caught-alaska-seafood-subscription-6-species",
);

const productHandleToProductCache = new Map();

class SeafoodPlanData {
  constructor(storedDataString) {
    if (storedDataString) {
      const data = JSON.parse(storedDataString);
      // The shopper's persisted choice (n2t_freebie_selection) — never
      // Shopify's discount_code cookie, which checkout consumes (D5).
      this.discountCookie = window?.discountManager?.getSelectedFreebie?.() || data.discountCookie;
      this.seafoodType = data.seafoodType === "custom" ? "salmon" : data.seafoodType;
      this.planFrequency = data.planFrequency || 4;
      this.sellingPlanId = BOX_WEEKS_TO_PLAN_ID.get(
        String(this.planFrequency) || 4,
      );
      this.boxSize = data.boxSize;
      this.productHandle = data.seafoodType === "custom" ? "wild-caught-alaskan-salmon-box" : (SEAFOOD_TYPE_TO_PRODUCT_HANDLE.get(this.seafoodType));
    } else {
      this.seafoodType = "fishvariety"; // salmon, whitefish, crab, fishvariety
      this.planFrequency = "4"; // 4, 6, 8
      this.boxSize = "4.5 lbs"; // 4.5 lbs, 9 lbs
      this.discountCookie = window?.discountManager?.getSelectedFreebie?.() || "FREESMOKEDSALMON"; // active offer keys: FREESMOKEDSALMON, FREESCALLOPS, FREEJERKY (PR B — offer KEY only, never written as a checkout discount code)
      this.sellingPlanId = "693379957010"; // 693379957010, 693380022546, 693380088082
      this.productHandle = SEAFOOD_TYPE_TO_PRODUCT_HANDLE.get(this.seafoodType); // alaskan-whitefish-subscription-box, alaskan-crab-variety-subscription-box, alaskan-fish-variety-subscription-box
    }
  }

  selectBoxSize(boxSize) {
    this.boxSize = boxSize;
    sessionStorage.setItem(SEAFOOD_PLAN_KEY, JSON.stringify(this));
  }

  selectSeafood(seafoodType) {
    this.seafoodType = seafoodType;
    this.productHandle = SEAFOOD_TYPE_TO_PRODUCT_HANDLE.get(seafoodType);
    sessionStorage.setItem(SEAFOOD_PLAN_KEY, JSON.stringify(this));
  }

  selectDiscountCookie(cookie) {
    this.discountCookie = cookie;
    sessionStorage.setItem(SEAFOOD_PLAN_KEY, JSON.stringify(this));
  }

  selectPlanFrequency(planFrequency) {
    this.planFrequency = planFrequency;
    this.sellingPlanId = BOX_WEEKS_TO_PLAN_ID.get(
      String(this.planFrequency) || 4,
    );

    sessionStorage.setItem(SEAFOOD_PLAN_KEY, JSON.stringify(this));
  }
}

// Report a seafood-plan give-up to PostHog. Mirrors NttUtils.fetchWithRetry's
// fetch_failed shape, but covers the paths fetchWithRetry cannot see: a non-ok
// body, an HTML error page that fails JSON.parse ("Unrecognized token '<'"), a
// render throw, or an unhandled handler rejection. Without this the
// seafood-plan-product step read as zero rows while shoppers dead-ended on the
// box-size step. `stack` and `$exception_source` make the previously
// unattributable "Load failed" traceable to this script. Never throws; top-level
// + injectable for tests.
function reportSeafoodPlanFailure(step, error, deps) {
  deps = deps || {};
  const capture =
    deps.capture ||
    (typeof window !== "undefined" &&
    window.posthog &&
    typeof window.posthog.capture === "function"
      ? window.posthog.capture.bind(window.posthog)
      : null);
  try {
    if (typeof capture === "function") {
      // sendBeacon + send_instantly so the event still leaves when the give-up
      // is the page tearing down (which also cancels PostHog's async request).
      capture(
        "fetch_failed",
        {
          step: step,
          url: deps.url || "",
          message: (error && error.message) || String(error),
          stack: (error && error.stack) || "",
          $exception_source: "seafood-plan-single-flow.js",
          path:
            (typeof window !== "undefined" &&
              window.location &&
              window.location.pathname) ||
            "",
        },
        { transport: "sendBeacon", send_instantly: true },
      );
    }
  } catch (telemetryError) {
    if (typeof console !== "undefined" && console.debug) {
      console.debug("fetch_failed capture skipped", telemetryError);
    }
  }
}

// Resilient product fetch for the seafood-plan flow. Routes through
// NttUtils.fetchWithRetry so a transient iOS in-app-webview fetch abort
// ("TypeError: Load failed") retries instead of silently dead-ending the box
// cards. Returns the parsed product, or null on give-up / non-ok (never throws)
// so callers degrade gracefully. Top-level + injectable so it is unit-testable.
async function fetchProductByHandle(handle, deps) {
  deps = deps || {};
  const root =
    deps.root != null
      ? deps.root
      : typeof window !== "undefined"
        ? window?.Shopify?.routes?.root
        : "/";
  const fetchWithRetry =
    deps.fetchWithRetry ||
    (typeof window !== "undefined" ? window?.NttUtils?.fetchWithRetry : null);
  const capture = deps.capture;
  const url = root + "products/" + handle + ".js";
  const report = (error) =>
    reportSeafoodPlanFailure("seafood-plan-product", error, { capture, url });

  const usedRetry = typeof fetchWithRetry === "function";
  try {
    let response;
    if (usedRetry) {
      response = await fetchWithRetry(
        url,
        { method: "GET" },
        { retries: 2, backoffMs: 300, step: "seafood-plan-product" },
      );
    } else {
      // Fallback for runtimes without NttUtils.
      response = await fetch(url, { method: "GET" });
    }
    if (!response || !response.ok) {
      // fetchWithRetry hands back non-ok <500 without reporting, so this branch
      // is invisible unless we report it here.
      report(new Error("HTTP " + (response ? response.status : "no response")));
      captureSeafoodPlanProgress("box_options", "blocked", {
        operation: "fetch_product",
        status: response && Number.isInteger(response.status) ? response.status : 0,
      });
      return null;
    }
    let product;
    try {
      product = await response.json();
    } catch (parseError) {
      // A CDN/proxy HTML error page parsed as JSON: "Unrecognized token '<'".
      report(parseError);
      captureSeafoodPlanProgress("box_options", "blocked", {
        operation: "fetch_product",
        error_type: parseError && typeof parseError.name === "string" ? parseError.name : "UnknownError",
      });
      return null;
    }
    return product || null;
  } catch (error) {
    // fetchWithRetry already emits fetch_failed on its own give-up, so only
    // report here when the NttUtils-less fallback fetch threw, avoiding duplicates.
    if (!usedRetry) {
      report(error);
    }
    captureSeafoodPlanProgress("box_options", "blocked", {
      operation: "fetch_product",
      error_type: error && typeof error.name === "string" ? error.name : "UnknownError",
    });
    return null;
  }
}

// Resolve a discount cookie value to its checkout urlPath via the discount
// manager's cookie->info map. Returns null when the value or map is missing
// instead of dereferencing `.urlPath` on undefined (PostHog:
// "undefined is not an object (evaluating 'discountInfo.urlPath')"). Top-level
// so it is unit-testable.
function resolveDiscountUrlPath(discountManager, value) {
  const map = discountManager && discountManager.discountCookieToInfoMap;
  if (!map || typeof map.get !== "function") {
    return null;
  }
  const info = map.get(value);
  return info && info.urlPath != null ? info.urlPath : null;
}

// Selection priority for page setup: EXPLICIT CHOICE | URL param (only when no
// prior choice exists, so ?freeoffer= email links still pre-select) | STORAGE/
// DEFAULT. Shopify's discount_code cookie is never read for selection —
// checkout consumes it, which is how the chosen freebie used to swap back to
// the default (D2/D5). Top-level + injectable for vitest.
function resolveDefaultFreebieSelection(opts) {
  opts = opts || {};
  return (
    opts.persisted ||
    (opts.urlParam ? String(opts.urlParam).toUpperCase() : undefined) ||
    opts.storedCookie ||
    undefined
  );
}

// The one read path for "which freebie is active": the persisted, map-validated
// choice, else the in-memory plan state (seeded default, or "affiliate"). The
// checked card, the price descriptor, and the checkout payload all resolve
// through here so they can never disagree. Top-level + injectable for vitest.
function resolveActiveSelection(discountManager, planData) {
  const persisted =
    discountManager && typeof discountManager.getSelectedFreebie === "function"
      ? discountManager.getSelectedFreebie()
      : undefined;
  return persisted || (planData && planData.discountCookie) || undefined;
}

// Cart lines the checkout reconcile owns (NttUtils.removeFlowLines): freebies +
// the Owens insert by variant id, subscription boxes by product handle (their
// variant ids vary by size/frequency). Untagged matches sweep up lines added by
// pre-hotfix code so they can't stack against tagged re-adds. Dropped-offer
// variants (halibut, dip/spread) stay listed on purpose: the sweep must still
// remove legacy lines added before those offers were retired (PR B).
const SEAFOOD_FLOW_LINE_SPEC = {
  variantIds: [
    "50515509444882", // FREEHALIBUT
    "50230576513298", // FREESMOKEDSALMON
    "50230578774290", // FREESALMONDIP / FREESALMONSPREAD
    "51789329236242", // FREESCALLOPS
    "51732697055506", // FREEJERKY
    "50989025853714", // Family Owens insert
  ],
  productHandles: Array.from(SEAFOOD_TYPE_TO_PRODUCT_HANDLE.values()),
};

// How the checkout treats offers, by the section's offer_mode setting. "freebie" is the
// Seafood Plan page: the picked free gift, lines tagged seafood-plan, and any free sockeye
// from elsewhere removed (one offer per order). "sockeye" is the Black Friday 2026 page
// (Luke + Iiley, 2026-10-27): no gift, no scallops, lines tagged black-friday so the cart
// observer (assets/cart-entitlement.js) gives the box the free sockeye, and the checkout
// waits for that reconcile because it redirects straight to /checkout.
// Top-level + injectable for vitest.
const BLACK_FRIDAY_FLOW_KEY = "black-friday";
function checkoutOfferPlan(offerMode) {
  if (offerMode === "sockeye") {
    return { flowTag: BLACK_FRIDAY_FLOW_KEY, addFreebie: false, removeSockeye: false, writeDiscount: false, reconcileSockeye: true };
  }
  return { flowTag: SEAFOOD_PLAN_KEY, addFreebie: true, removeSockeye: true, writeDiscount: true, reconcileSockeye: false };
}

function readOfferMode(doc) {
  const el = doc && doc.getElementById && doc.getElementById("sub-builder");
  return (el && el.dataset && el.dataset.offerMode) || "freebie";
}

// Lets the cart observer add the free sockeye before the redirect. Capped so a slow or
// failed reconcile never strands the shopper; never rejects. Mirrors product-form.js.
function reconcileOfferBeforeCheckout(win, capMs) {
  const entitlement = win && win.N2TCartEntitlement;
  if (!entitlement || typeof entitlement.reconcileNow !== "function") return Promise.resolve();
  return Promise.race([
    Promise.resolve()
      .then(() => entitlement.reconcileNow())
      .catch(() => {}),
    new Promise((resolve) => setTimeout(resolve, capMs == null ? 4000 : capMs)),
  ]);
}

// Removes every discount code saved on the cart (Cart API: `discount: ""` clears them). Only
// posts when the cart holds one. Never throws: a failed clear must not block checkout, and the
// Shopify Flow backstop still sees the order.
async function clearCartDiscountCodes(cart, fetchImpl) {
  const codes = (cart && cart.discount_codes) || [];
  if (!codes.length) return false;
  const doFetch = fetchImpl || (typeof fetch === "function" ? fetch : null);
  if (!doFetch) return false;
  try {
    const root = (typeof window !== "undefined" && window?.Shopify?.routes?.root) || "/";
    const res = await doFetch(root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ discount: "" }),
    });
    return !!(res && res.ok);
  } catch {
    return false;
  }
}

function lookForDiscountManager(resolve) {
  if (window?.discountManager) {
    resolve();
  } else {
    setTimeout(() => {
      lookForDiscountManager(resolve);
    }, 100);
  }
}
var __N2T_IS_NODE_TEST =
  typeof process !== 'undefined' && process.versions != null && process.versions.node != null;
if (!__N2T_IS_NODE_TEST) {
// bfcache guard (D3): a back-forward-cache restore resumes a frozen JS heap
// with stale selection and cart state. Reload re-runs setup against the
// persisted selection and live /cart.js state. No other listener in the theme
// handles pageshow today (grep-confirmed).
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    location.reload();
  }
});
new Promise((resolve) => {
lookForDiscountManager(resolve);
}).then(()=> {


const storedSeafoodPlan = sessionStorage.getItem(SEAFOOD_PLAN_KEY);
const seafoodPlanData = new SeafoodPlanData(storedSeafoodPlan);
let frequencyTouched = false;
// Frequency-default experiment. Only a shopper starting fresh this session is
// enrolled; anyone carrying a stored plan keeps the frequency they have. Box
// cards wait for this (at most FREQUENCY_DEFAULT_FLAG_TIMEOUT_MS) before their
// first render, so nobody sees control prices and is then switched.
// The section's data-frequency-experiment="live" is the launch switch: without
// it nobody waits for, or reads, the flag.
const frequencyExperimentLive = !!document.querySelector('.delivery[data-frequency-experiment="live"]');
// Ineligible once the shopper picked a frequency, or once the section's
// failsafe revealed the control summary (they have already seen control).
const frequencyStillEligible = () =>
  !frequencyTouched && !document.querySelector(".delivery[data-frequency-failsafe]");
const frequencyAssignment = storedSeafoodPlan || !frequencyExperimentLive
  ? Promise.resolve()
  : resolveFrequencyDefaultVariant(
      window.posthog,
      FREQUENCY_DEFAULT_FLAG_TIMEOUT_MS,
      () => frequencyStillEligible(),
    ).then((variant) => {
      try {
        const weeks = frequencyWeeksForVariant(variant);
        if (weeks && frequencyStillEligible() && String(seafoodPlanData.planFrequency) !== weeks) {
          seafoodPlanData.selectPlanFrequency(weeks);
        }
      } catch (_) { /* keep the default frequency */ }
    });
function frequencyLabel(weeks) {
  // .data("weeks") hands back a number, so compare as strings.
  switch (String(weeks)) {
    case "4": return "Every month";
    case "6": return "Every 1.5 Months";
    case "8": return "Every Other Month";
    case "16": return "Every Two Months";
    default: return "Every " + weeks + " weeks";
  }
}
// The latest box-card render. Checkout waits on it, so a tap while the cards are
// still loading can't submit the seeded "4.5 lbs" placeholder as a variant id.
let boxCardsReady = Promise.resolve();
$(document).ready(function () {
  // fresh_entrant mirrors the experiment's eligibility check (no stored plan),
  // so eligible traffic can be counted before launch.
  captureSeafoodPlanProgress("plan", "viewed", { fresh_entrant: !storedSeafoodPlan });
  // While the experiment is live, an inline script in the section hides the
  // "Every Month" summary before first paint for fresh entrants. Label it from
  // the assignment, then show it, so a six-weeks shopper never sees control.
  frequencyAssignment.then(() => {
    $(".timeFrame").text(frequencyLabel(seafoodPlanData.planFrequency));
    $(".delivery").css("visibility", "");
  });
  /*******************************************
   * State
   *******************************************/
  /*******************************************
   * Seafood Plan Logic
   *******************************************/
  // Listen for seafood selection
  $("input[name='seafood']").on("click", async function () {
    // Wrap the whole async handler: a throw inside it (e.g. a null
    // scrollIntoView target, or a render throw from updateBoxSizeCards) would
    // otherwise surface as an unhandled rejection with $exception_source: null,
    // exactly the untraceable "Load failed" this page reports.
    try {
      var seafoodType = $("input[name=seafood]:checked").val();
      seafoodPlanData.selectSeafood(seafoodType);
      captureSeafoodPlanProgress("seafood", "selected", {
        seafood_type: SEAFOOD_TYPE_TO_PRODUCT_HANDLE.has(seafoodType) ? seafoodType : "custom",
      });

      showCorrectSectionsForSeafoodPlan(seafoodType);

      if (seafoodType !== "custom") {
        // The Black Friday page (offer_mode sockeye) renders no free-gift section.
        const freebieSection = document.querySelector("#seafood-freebie");
        if (freebieSection) freebieSection.scrollIntoView({ behavior: "smooth" });

        await loadBoxSizeCards();
      }
    } catch (error) {
      reportSeafoodPlanFailure("seafood-plan-seafood-click", error);
      showBoxSizeError();
    }
  });

  function showCorrectSectionsForSeafoodPlan(seafoodType) {
    if (seafoodType === "custom") {
      const customBoxButton = document.querySelector(
        ".mainButtonArea .custom-box-button",
      );
      customBoxButton.classList.remove("hidden");
      const checkoutButton = document.querySelector(
        ".mainButtonArea .checkout-button",
      );
      checkoutButton.classList.add("hidden");
      const boxSizeSection = document.getElementById("sub-order-summary");
      boxSizeSection.classList.add("hidden");
      const freebieSection = document.getElementById("freebie-area");
      freebieSection?.classList.add("hidden");
    } else {
      const customBoxButton = document.querySelector(
        ".mainButtonArea .custom-box-button",
      );
      customBoxButton.classList.add("hidden");
      const checkoutButton = document.querySelector(
        ".mainButtonArea .checkout-button",
      );
      checkoutButton.classList.remove("hidden");
      const boxSizeSection = document.getElementById("sub-order-summary");
      boxSizeSection.classList.remove("hidden");
      const freebieSection = document.getElementById("freebie-area");
      freebieSection?.classList.remove("hidden");
    }
  }

  /*******************************************
   * Box Size & Schedule Logic
   *******************************************/
  $(document).on("click", ".changeTime", function () {
    $(".changeTime").hide();
    $(".time-links").show();
  });

  // On selecting a frequency option
  $(document).on("click", ".time-link", async function () {
    var weeks = $(this).data("weeks");
    frequencyTouched = true;
    seafoodPlanData.selectPlanFrequency(weeks);
    captureSeafoodPlanProgress("frequency", "selected", {
      weeks: ["4", "6", "8"].includes(String(weeks)) ? Number(weeks) : 0,
    });
    $(".time-links").hide();
    $(".changeTime").show();
    await loadBoxSizeCards();
  });

  $(document).on(" click", "input[name='boxsize']", function (e, others) {
    var variantId = $("input[name=boxsize]:checked").val();
    seafoodPlanData.selectBoxSize(variantId);

    if (!others?.isScriptedClick) {
      captureSeafoodPlanProgress("box_size", "selected");
      document.querySelector('.mainButtonArea .continueButton').classList.add('ready') 
    }
  });

  async function getProductPlanInfo() {
    console.log("getProductPlanInfo", seafoodPlanData.productHandle);
    const handle = seafoodPlanData.productHandle;
    if (productHandleToProductCache.has(handle)) {
      return productHandleToProductCache.get(handle);
    }

    const product = await fetchProductByHandle(handle);
    if (product) {
      productHandleToProductCache.set(handle, product);
    }
    return product;
  }

  /*******************************************
   * Freebie Logic
   *******************************************/

  // Listen for freebie selection
  $("input[name='offer-value']").on("click", function () {
    var offerChoice = $("input[name='offer-value']:checked").val();
    captureSeafoodPlanProgress("offer", "selected", {
      offer_type: offerChoice === "affiliate" ? "affiliate" : "promotion",
    });
    // The genuine radio click is the ONLY place the choice persists. The setup
    // paths (selectDefaultFreebieCard / setupAffilateDiscountCard) also funnel
    // through onFreebieSelected — persisting there would pin the hard-coded
    // default on first load and kill ?freeoffer= email links.
    window.discountManager.persistSelection(offerChoice);
    onFreebieSelected(offerChoice);
          document
        .querySelector("#sub-order-summary")
        .scrollIntoView({ behavior: "smooth" });
      
  });

  async function onFreebieSelected(offerChoice) {
      

    if (offerChoice === "affiliate") {
      // This only happens if the user has an affiliate discount code and then selects a different offer.
      // and then selects the affiliate offer again.
      seafoodPlanData.selectDiscountCookie(offerChoice);
      window.discountManager.setDiscountCode("affiliate");
    } else {
      seafoodPlanData.selectDiscountCookie(offerChoice);
      const resolvedUrlPath = resolveDiscountUrlPath(window.discountManager, offerChoice);
      if (resolvedUrlPath) {
        window.discountManager.setDiscountCode(resolvedUrlPath);
      }
    }


    await loadBoxSizeCards();
  }

  // One entry point for (re)building the box-size cards. Every path that rebuilds
  // the cards routes through here so the give-up is handled once: instead of the
  // old silent console.error that left the dead Liquid skeleton cards in place,
  // it surfaces a retryable error toast so the shopper isn't stranded on
  // unclickable cards. A render throw is caught and reported, not left to become
  // an unhandled rejection.
  function loadBoxSizeCards() {
    boxCardsReady = renderBoxSizeCards();
    return boxCardsReady;
  }

  async function renderBoxSizeCards() {
    const [product] = await Promise.all([getProductPlanInfo(), frequencyAssignment]);
    if (!product) {
      // fetchProductByHandle already emitted fetch_failed for the give-up.
      showBoxSizeError();
      return;
    }
    try {
      updateBoxSizeCards(product);
    } catch (error) {
      reportSeafoodPlanFailure("seafood-plan-render", error);
      showBoxSizeError();
    }
  }

  function showBoxSizeError() {
    window.NttUtils.showCartError(
      "We couldn't load your box options. Please check your connection and try again.",
      { onRetry: loadBoxSizeCards },
    );
  }

  function setupAffilateDiscountCard() {
    const affilateCard = $(".sub-select-card.affiliate");
    affilateCard.removeClass("hidden");
    const nameElem = affilateCard.find("[data-affiliate-property='name'] span");
    const descriptionElem = affilateCard.find(
      "[data-affiliate-property='description'] span",
    );

    descriptionElem.text("Your promotional discount will apply at checkout.");
    nameElem.text("Special Promo");

    $(".sub-select-card.affiliate input").prop("checked", true);
    onFreebieSelected("affiliate");
  }

  /*******************************************
   * Page Setup Logic
   *******************************************/
  function selectDefaultFreebieCard() {
    console.log("default freebie: discount info map: ", window.discountManager.discountCookieToInfoMap);
    var free_offer_param = window.NttUtils.getUrlParameter("freeoffer");

    // Priority: EXPLICIT CHOICE | URL (no prior choice) | STORAGE | DEFAULT.
    // Never read Shopify's discount_code cookie for selection (checkout
    // consumes it, and that consumption re-armed the default — D2/D5).
    var defaultDiscountVal = resolveDefaultFreebieSelection({
      persisted: window.discountManager.getPersistedSelection(),
      urlParam: free_offer_param,
      storedCookie: seafoodPlanData.discountCookie,
    });
      console.log('default discount val:', defaultDiscountVal);
    const discountMap = window.discountManager.discountCookieToInfoMap;
    const discountInfo = discountMap && discountMap.get(defaultDiscountVal);

    const resolvedUrlPath = resolveDiscountUrlPath(window.discountManager, defaultDiscountVal);
    if (resolvedUrlPath) {
      window.discountManager.setDiscountCode(resolvedUrlPath);
    }
    if (discountInfo) {
      $(".sub-select-card." + (discountInfo.name || "") + " input").prop(
        "checked",
        true,
      );
    }
    // Pass the resolved selection itself — currentDiscount is undefined until
    // the freeOffers map builds, and feeding that back used to blank the
    // selection state on slow loads.
    onFreebieSelected(defaultDiscountVal);
  }

  function setupFreebieDefaults() {
    if (window.discountManager.hasAffiliateCode()) {
      setupAffilateDiscountCard();
    } else {
      selectDefaultFreebieCard();
    }
  }

  function setupDefaultSeafoodPlanCards() {
    const seafoodPlanData = new SeafoodPlanData(
      sessionStorage.getItem(SEAFOOD_PLAN_KEY),
    );
    console.log("seafood plan data from storage is:", seafoodPlanData);

    let seafoodType = seafoodPlanData.seafoodType;
    console.log("seafoodType", seafoodType)
    // var seafoodParam = getUrlParameter("seafoodtype");
    // console.log("seafoodparam:", seafoodParam);

    // if (seafoodParam) {
    //   seafoodType = seafoodParam;
    // }

    // if (seafoodType === "custom") {
    //   seafoodType = "salmon";
    // }
    
    // if (!seafoodType) {
    //   seafoodType = "fishvariety";
    // }
        console.log("seafoodType", seafoodType)
    console.log("trying to select seafood type:", seafoodType, $(".sub-select-card." + seafoodType + " input"))

    $(".sub-select-card." + seafoodType + " input").prop("checked", true);

    seafoodPlanData.selectSeafood(seafoodType);
    showCorrectSectionsForSeafoodPlan(seafoodType);
  }

  async function setupDefaultBoxSizeCards() {
    console.log("setupDefaultBoxSizeCards");
    $(".time-links").hide();
    await loadBoxSizeCards();
  }

  function setupDefaults() {
    setupDefaultSeafoodPlanCards();
    setupDefaultBoxSizeCards();
    if (document.querySelector(".freebie-section")) {
      setupFreebieDefaults();
    } 
  }

  /*******************************************
   * Checkout Logic
   *******************************************/
  // Re-entry guard for double-clicks and the error toast's .trigger("click")
  // retry — a plain flag rather than the disabled attribute, because jQuery
  // .trigger bypasses disabled.
  let checkoutBusy = false;
  $(".checkout-button").click(async function () {
    if (checkoutBusy) return;
    checkoutBusy = true;
    this.classList.add("is-busy");
    captureSeafoodPlanProgress("checkout", "attempted");
    let checkoutStage = "await_box_cards";
    try {
      // Wait for the latest render: a seafood change mid-wait replaces it.
      let awaitedRender;
      do {
        awaitedRender = boxCardsReady;
        await awaitedRender;
      } while (awaitedRender !== boxCardsReady);
      if (!/^\d+$/.test(String(seafoodPlanData.boxSize))) {
        const notReady = new Error("No box size selected yet");
        notReady.name = "BoxSizeNotReady";
        throw notReady;
      }
      checkoutStage = "prepare_items";
      // Snapshot the cadence with the items, so a frequency change while the
      // cart requests run can't make cart_ready report a plan that wasn't sent.
      const cartWeeks = ["4", "6", "8"].includes(String(seafoodPlanData.planFrequency))
        ? Number(seafoodPlanData.planFrequency) : 0;
      // Derive the discount from the validated selection, never from the
      // in-memory currentDiscount (undefined until the freeOffers map builds —
      // that used to write an empty cookie and add no freebie).
      const selection = resolveActiveSelection(window.discountManager, seafoodPlanData);
      const offerPlan = checkoutOfferPlan(readOfferMode(document));

      const items = offerPlan.addFreebie ? window.discountManager.freebieItemsFor(selection) : [];
      items.push({
        id: seafoodPlanData.boxSize,
        quantity: 1,
        selling_plan: seafoodPlanData.sellingPlanId,
        properties: { _n2t_flow: offerPlan.flowTag },
      });
      items.push({
        id: "50989025853714",
        quantity: 1,
        properties: { _n2t_flow: offerPlan.flowTag },
      });

      // Reconcile, don't wipe (D1): remove only the lines this flow owns —
      // tagged ones plus untagged pre-hotfix flow lines — so anything else in
      // the cart survives, and an interruption leaves the previous valid cart
      // instead of an empty one.
      checkoutStage = "read_cart";
      const cart = await window.NttUtils.getCart();
      checkoutStage = "reconcile_cart";
      await window.NttUtils.removeFlowLines(cart, SEAFOOD_FLOW_LINE_SPEC);
      // One offer per order: this page's offer is the free scallops, so a free sockeye
      // picked up on Build a Box or a product page comes out (Luke, 2026-10-27).
      // Read the cart again first: removing discounted flow lines can rotate the
      // remaining line keys, so the first snapshot's sockeye key may be stale.
      if (
        offerPlan.removeSockeye &&
        typeof window.NttUtils.removeOfferLines === "function" &&
        typeof window.NttUtils.isOfferLine === "function" &&
        (cart.items || []).some(window.NttUtils.isOfferLine)
      ) {
        checkoutStage = "remove_offer";
        await window.NttUtils.removeOfferLines(await window.NttUtils.getCart());
      }

      checkoutStage = "apply_offer";
      // The Black Friday offer is never combined with a code: a leftover discount_code
      // cookie (WELCOME30 from the Seafood Plan page's $30 card lasts 7 days) would apply.
      if (offerPlan.writeDiscount) window.discountManager.writeCheckoutDiscountCookie(selection);
      else {
        window.discountManager.clearDiscountCookie();
        // The cookie is only half of it: Shopify also keeps a code ON THE CART (a /discount/
        // link, an earlier Seafood Plan checkout), and checkout applies that one too
        // (NT-10595, 2026-11-03: WELCOME30 on a Black Friday order after the cookie fix).
        await clearCartDiscountCodes(cart);
      }

      // One POST for freebie + box + insert: three sequential mutations become
      // one, which also shrinks the Intelligems /cart/update.js race (D6).
      checkoutStage = "add_items";
      await window.NttUtils.addItemsToCart(items);
      if (offerPlan.reconcileSockeye) {
        checkoutStage = "reconcile_offer";
        await reconcileOfferBeforeCheckout(window);
      }
      captureSeafoodPlanProgress("checkout", "cart_ready", { weeks: cartWeeks });
      checkoutStage = "redirect";
      location.href = "/checkout";
    } catch (error) {
      captureSeafoodPlanProgress("checkout", "blocked", {
        operation: checkoutStage,
        error_type: error && typeof error.name === "string" ? error.name : "UnknownError",
      });
      // A fetch aborted or a cart write came back non-ok (commonly an iOS
      // in-app-browser abort). Surface a retryable error so the shopper isn't
      // stranded on a dead button.
      window.NttUtils.showCartError(
        "We couldn't start your checkout. Please check your connection and try again.",
        { onRetry: function () { $(".checkout-button").trigger("click"); } },
      );
    } finally {
      checkoutBusy = false;
      this.classList.remove("is-busy");
    }
  });

  /*******************************************
   * UI Manipulation Functions
   *******************************************/
  function updateBoxSizeCards(product) {
    const $cards = $("#product-customizer-cards");

    // Remember the pack the shopper currently has selected before we clear and
    // rebuild — its "12"/"24" portion label survives a rebuild even when the
    // variant id does not (a different seafood type renders different variants).
    const priorPackSize = $cards
      .find("input[name=boxsize]:checked")
      .closest(".sub-select-card")
      .find(".portion-text span")
      .text();

    $cards.html("");

    const skioSellingPlanGroups = product.selling_plan_groups.filter(
      (group) => group.app_id === "SKIO",
    );

    for (var i = 0; i < product.variants.length; i++) {
      buildBoxSizeCard(product, product.variants[i], skioSellingPlanGroups);
    }

    // Re-select the shopper's box now that the cards are rebuilt. Match the exact
    // variant id first; if it isn't in this render, keep the same pack size so a
    // freebie/frequency/seafood change can't snap a 24-Pack shopper back to the
    // 12-Pack (the seeded "4.5 lbs" default is never a variant id, so it lands
    // here too). Fall back to the first card only when neither resolves.
    var $target = $();
    if (seafoodPlanData.boxSize != null) {
      // Match the card's id, set from data-id (loadTemplate binds data-value via
      // .val(), which sets the value property, not an attribute a selector sees).
      $target = $("#" + seafoodPlanData.boxSize);
    }
    if (!$target.length && priorPackSize) {
      $target = $cards
        .find(".sub-select-card")
        .filter(function () {
          return $(this).find(".portion-text span").text() === priorPackSize;
        })
        .find("input[name=boxsize]");
    }
    if (!$target.length) {
      $target = $cards.find(".sub-select-card:first-child input");
    }
    $target
      .first()
      .prop("checked", true)
      .trigger("click", [{ isScriptedClick: true }]);

    // Show 20% off if applicable
    if (resolveActiveSelection(window.discountManager, seafoodPlanData) === "WELCOME20") {
      $(".additional20").removeClass("hidden");
    }
  }

  function buildBoxSizeCard(product, variant, skioSellingPlanGroups) {
    if (!variant.available) {
      return;
    }

    var active_selling_plan = null;

    for (var j = 0; j < variant.selling_plan_allocations.length; j++) {
      var alloc = variant.selling_plan_allocations[j];
      const isSkio = skioSellingPlanGroups.some(
        (group) => group.id === alloc.selling_plan_group_id,
      );

      if (
        String(alloc.selling_plan_id) == seafoodPlanData.sellingPlanId &&
        isSkio
      ) {
        active_selling_plan = variant.selling_plan_allocations[j];
      }
    }

    if (!active_selling_plan) {
      return;
    }

    var timeFrame = frequencyLabel(seafoodPlanData.planFrequency);

    var totalWeight = 0;
    var meals = 0;
    var priceExtraText = "";
    // Fix #256: keep the preview discount in a LOCAL variable. The product
    // (and its selling_plan_allocations) comes from productHandleToProductCache,
    // so writing the discounted price back onto active_selling_plan.price made
    // every re-render (freebie/frequency/seafood-type click) subtract another
    // $20 from the already-discounted cached value.
    var previewedPriceCents = active_selling_plan.price;
    // The Black Friday 2026 page (offer_mode "sockeye") previews no code: its offer is
    // the free sockeye, not a discount. This replaces the 2025 "black-friday" URL check
    // that previewed 100OFF.
    var codeCookieValue = checkoutOfferPlan(readOfferMode(document)).writeDiscount
      ? resolveActiveSelection(window.discountManager, seafoodPlanData)
      : undefined;
    if (codeCookieValue) {
      const info =
        window.discountManager.discountCookieToInfoMap.get(codeCookieValue);
      if (info) {
        priceExtraText = info.priceDescriptor;
      }

      previewedPriceCents = applyPreviewDiscountCents(
        previewedPriceCents,
        codeCookieValue,
      );
    }

    var priceNumber = previewedPriceCents / 100;

    // Fix #264: 2 decimals so the funnel card matches the cart ($142.45, not $142).
    var priceFormatted = "$" + window.NttUtils.formatMoney(priceNumber, 2);
    var priceCompareString =
      variant.price != previewedPriceCents
        ? "$" + window.NttUtils.formatMoney(variant.price / 100, 2)
        : "";

    if (variant.title.includes("4.5 lbs") && !variant.title.includes("Only")) {
      meals = 12;
      totalWeight = "4.5 lbs";
    } else if (variant.title.includes("9 lbs")) {
      meals = 24;
      totalWeight = "9 lbs";
    }

    if (meals > 0) {
      var packTitle = totalWeight + " " + product.title;

      $("#product-customizer-cards").loadTemplate(
        $("#box-size-card-template"),
        {
          variantId: variant.id,
          sellingPlan: seafoodPlanData.sellingPlanId,
          portions: meals,
          packTitle: packTitle,
          originalPrice: priceCompareString,
          price: priceFormatted,
          mainImage: product.featured_image,
          priceExtra: priceExtraText,
        },
        { append: true, async: true },
      );

      $(".time-link").removeClass("active");
      $("#" + seafoodPlanData.planFrequency + "weeks").addClass("active");

      $(".timeFrame").text(timeFrame);
    }
  }


  setupDefaults(seafoodPlanData);
});

});
} // end browser-only bootstrap guard

// Node-only test export.
if (
  typeof module === 'object' && module !== null &&
  typeof module.exports === 'object' &&
  typeof process !== 'undefined' && process.versions != null && process.versions.node != null
) {
  module.exports = {
    SeafoodPlanData,
    BOX_WEEKS_TO_PLAN_ID,
    SEAFOOD_TYPE_TO_PRODUCT_HANDLE,
    SEAFOOD_FLOW_LINE_SPEC,
    fetchProductByHandle,
    reportSeafoodPlanFailure,
    resolveDiscountUrlPath,
    resolveDefaultFreebieSelection,
    resolveActiveSelection,
    applyPreviewDiscountCents,
    WELCOME20_FLAT_OFF_CENTS,
    FREQUENCY_DEFAULT_FLAG,
    frequencyWeeksForVariant,
    resolveFrequencyDefaultVariant,
    BLACK_FRIDAY_FLOW_KEY,
    checkoutOfferPlan,
    readOfferMode,
    reconcileOfferBeforeCheckout,
    clearCartDiscountCodes,
  };
}
